class CartDrawer {
  constructor() {
    this.selectors = {
      cartDrawer: '#cart-drawer',
      cartToggle: '[data-cart-toggle]',
      cartCount: '[data-cart-count]',
      addToCartButton: '[data-add-to-cart]',
      quantityDecrease: '.cart-drawer__quantity-decrease',
      quantityIncrease: '.cart-drawer__quantity-increase',
      quantityInput: '.cart-drawer__quantity-input',
      removeItem: '.cart-drawer__remove',
      checkout: '.cart-drawer__checkout',
      continueShopping: '.cart-drawer__continue-shopping',
      cartItems: '.cart-drawer__items',
    };

    this.classes = {
      active: 'active',
      loading: 'loading',
    };

    // Store bound methods to allow proper event removal
    this.boundCloseDrawer = this.closeDrawer.bind(this);
    this.boundDecreaseQuantity = this.decreaseQuantity.bind(this);
    this.boundIncreaseQuantity = this.increaseQuantity.bind(this);
    this.boundRemoveItem = this.removeItem.bind(this);
    this.boundToggleDrawer = this.toggleDrawer.bind(this);

    this.init();
  }

  /**
   * Initialize the cart drawer
   */
  init() {
    // Create overlay element if it doesn't exist
    if (!document.querySelector('.cart-drawer-overlay')) {
      const overlay = document.createElement('div');
      overlay.classList.add('cart-drawer-overlay');
      document.body.appendChild(overlay);
    }

    this.drawer = document.querySelector(this.selectors.cartDrawer);
    this.overlay = document.querySelector('.cart-drawer-overlay');

    // If no drawer found, fetch it via AJAX
    if (!this.drawer) {
      this.fetchCartDrawer();
    } else {
      this.bindEvents();
    }

    // Bind add to cart forms
    this.bindAddToCartForms();
  }

  /**
   * Fetch the cart drawer content via AJAX
   */
  fetchCartDrawer() {
    fetch('/?sections=cart-drawer')
      .then((response) => response.json())
      .then((sections) => {
        // Create drawer if it doesn't exist
        if (sections['cart-drawer']) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections['cart-drawer'];

          // Append drawer to body
          document.body.appendChild(tempDiv.firstElementChild);

          // Initialize after drawer is added
          this.drawer = document.querySelector(this.selectors.cartDrawer);
          this.bindEvents();
        }
      })
      .catch((error) => console.error('Error fetching cart drawer:', error));
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // First remove existing event listeners to prevent duplicates
    this.unbindEvents();

    // Toggle drawer
    document.querySelectorAll(this.selectors.cartToggle).forEach((button) => {
      button.addEventListener('click', this.boundToggleDrawer);
    });

    // Close drawer
    const closeButton = this.drawer.querySelector('.cart-drawer__close');
    if (closeButton) {
      // Use a timeout to ensure the DOM is fully updated before binding
      setTimeout(() => {
        closeButton.addEventListener('click', this.boundCloseDrawer);
      }, 10);
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.boundCloseDrawer);
    }

    // Checkout button
    const checkoutButton = this.drawer.querySelector(this.selectors.checkout);
    if (checkoutButton) {
      checkoutButton.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }

    // Continue shopping button
    const continueButton = this.drawer.querySelector(this.selectors.continueShopping);
    if (continueButton) {
      continueButton.addEventListener('click', this.boundCloseDrawer);
    }

    // Quantity buttons and remove buttons
    this.bindCartItemEvents();

    // Listen for ESC key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer();
      }
    });
  }

  /**
   * Remove event listeners to prevent duplicates
   */
  unbindEvents() {
    // Remove toggle drawer events
    document.querySelectorAll(this.selectors.cartToggle).forEach((button) => {
      button.removeEventListener('click', this.boundToggleDrawer);
    });

    // Remove close drawer events
    const closeButton = this.drawer?.querySelector('.cart-drawer__close');
    if (closeButton) {
      closeButton.removeEventListener('click', this.boundCloseDrawer);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.boundCloseDrawer);
    }

    // Remove quantity and remove button events
    this.unbindCartItemEvents();
  }

  /**
   * Bind events for cart item interactions
   */
  bindCartItemEvents() {
    // Decrease quantity
    this.drawer.querySelectorAll(this.selectors.quantityDecrease).forEach((button) => {
      button.addEventListener('click', this.boundDecreaseQuantity);
    });

    // Increase quantity
    this.drawer.querySelectorAll(this.selectors.quantityIncrease).forEach((button) => {
      button.addEventListener('click', this.boundIncreaseQuantity);
    });

    // Remove item
    this.drawer.querySelectorAll(this.selectors.removeItem).forEach((button) => {
      button.addEventListener('click', this.boundRemoveItem);
    });
  }

  /**
   * Remove events for cart item interactions
   */
  unbindCartItemEvents() {
    if (!this.drawer) return;

    // Remove decrease quantity events
    this.drawer.querySelectorAll(this.selectors.quantityDecrease).forEach((button) => {
      button.removeEventListener('click', this.boundDecreaseQuantity);
    });

    // Remove increase quantity events
    this.drawer.querySelectorAll(this.selectors.quantityIncrease).forEach((button) => {
      button.removeEventListener('click', this.boundIncreaseQuantity);
    });

    // Remove item events
    this.drawer.querySelectorAll(this.selectors.removeItem).forEach((button) => {
      button.removeEventListener('click', this.boundRemoveItem);
    });
  }

  /**
   * Bind add to cart form submissions
   */
  bindAddToCartForms() {
    document.querySelectorAll(this.selectors.addToCartButton).forEach((button) => {
      if (!button.closest('form')) {
        button.addEventListener('click', this.quickAdd.bind(this));
      }
    });
  }

  /**
   * Show loading state for a specific item
   * @param {HTMLElement} item - The cart item element
   */
  showItemLoading(item) {
    if (item) {
      item.classList.add(this.classes.loading);
    }
  }

  /**
   * Hide loading state for a specific item
   * @param {HTMLElement} item - The cart item element
   */
  hideItemLoading(item) {
    if (item) {
      item.classList.remove(this.classes.loading);
    }
  }

  /**
   * Remove all loading states
   */
  clearAllLoadingStates() {
    // Clear all item loading states
    if (this.drawer) {
      this.drawer.querySelectorAll('.cart-drawer__item').forEach((item) => {
        item.classList.remove(this.classes.loading);
      });

      // Clear button loading states
      this.drawer
        .querySelectorAll(
          [this.selectors.quantityDecrease, this.selectors.quantityIncrease, this.selectors.removeItem].join(',')
        )
        .forEach((button) => {
          button.classList.remove(this.classes.loading);
        });
    }
  }

  /**
   * Handle quick add button click
   * @param {Event} event - Click event
   */
  quickAdd(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const variantId = button.dataset.variantId;

    if (!variantId) {
      console.error('No variant ID provided for quick add');
      return;
    }

    button.classList.add(this.classes.loading);
    button.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          console.error('Error adding to cart:', data.description);
        } else {
          this.updateCartDrawer().then(() => {
            this.openDrawer();
          });
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      })
      .finally(() => {
        button.classList.remove(this.classes.loading);
        button.disabled = false;
      });
  }

  /**
   * Toggle the cart drawer
   * @param {Event} event - Click event
   */
  toggleDrawer(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.drawer.classList.contains(this.classes.active)) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  /**
   * Open the cart drawer
   */
  openDrawer() {
    // First update the cart drawer content
    this.updateCartDrawer().then(() => {
      // Short delay to ensure DOM is updated and events are bound
      setTimeout(() => {
        this.drawer.classList.add(this.classes.active);
        this.overlay.classList.add(this.classes.active);
        document.body.style.overflow = 'hidden';
      }, 10);
      this.updateCartCount();
    });
  }

  /**
   * Close the cart drawer
   */
  closeDrawer() {
    this.drawer.classList.remove(this.classes.active);
    this.overlay.classList.remove(this.classes.active);
    document.body.style.overflow = '';

    // Clear any remaining loading states
    this.clearAllLoadingStates();
  }

  /**
   * Update the cart drawer content
   * @returns {Promise} - Resolves when the cart drawer is updated
   */
  updateCartDrawer() {
    return fetch('/?sections=cart-drawer')
      .then((response) => response.json())
      .then((sections) => {
        if (sections['cart-drawer']) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections['cart-drawer'];

          // Replace inner HTML while keeping the drawer element
          const newContent = tempDiv.querySelector(this.selectors.cartDrawer).innerHTML;
          this.drawer.innerHTML = newContent;

          // Rebind events for the new content
          this.bindEvents();

        }
      })
      .catch((error) => {
        console.error('Error updating cart drawer:', error);
      });
  }

  /**
   * Update cart count displays throughout the site
   */
  // updateCartCount() {
  //   fetch('/cart.js')
  //     .then((response) => response.json())
  //     .then((cart) => {
  //       const count = cart.item_count;

  //       document.querySelectorAll(this.selectors.cartCount).forEach((element) => {
  //         element.textContent = count;

  //         // Hide/show based on count
  //         if (count === 0) {
  //           element.setAttribute('hidden', '');
  //         } else {
  //           element.removeAttribute('hidden');
  //         }
  //       });

  //       // Update cart count in header cart icon bubble
  //       const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
  //       if (cartCountBubble) {
  //         cartCountBubble.textContent = count;
  //       }

  //       // Show/hide the cart count bubble
  //       const cartCountBubbleContainer = document.querySelector('.cart-count-bubble');
  //       if (cartCountBubbleContainer) {
  //         if (count === 0) {
  //           cartCountBubbleContainer.style.display = 'none';
  //         } else {
  //           cartCountBubbleContainer.style.display = 'flex';
  //         }
  //       }
  //     })
  //     .catch((error) => console.error('Error updating cart count:', error));
  // }

  updateCartCount() {
    fetch('/?section_id=cart-icon-bubble')
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newEl = doc.querySelector('#shopify-section-cart-icon-bubble');
        const oldEl = document.querySelector('#cart-icon-bubble');

        if (newEl && oldEl) {
          oldEl.innerHTML = newEl.innerHTML;
        }
      })
      .catch(err => console.error('Error updating cart bubble:', err));
  }

  /**
   * Decrease item quantity
   * @param {Event} event - Click event
   */
  decreaseQuantity(event) {
    const button = event.currentTarget;
    // Add loading state to the clicked button
    button.classList.add(this.classes.loading);

    const line = parseInt(button.dataset.line, 10);
    const input = this.drawer.querySelector(`${this.selectors.quantityInput}[data-line="${line}"]`);
    let quantity = parseInt(input.value, 10) - 1;

    if (quantity < 1) {
      quantity = 1;
    }

    this.updateItemQuantity(line, quantity, button);
  }

  /**
   * Increase item quantity
   * @param {Event} event - Click event
   */
  increaseQuantity(event) {
    const button = event.currentTarget;
    // Add loading state to the clicked button
    button.classList.add(this.classes.loading);

    const line = parseInt(button.dataset.line, 10);
    const input = this.drawer.querySelector(`${this.selectors.quantityInput}[data-line="${line}"]`);
    const quantity = parseInt(input.value, 10) + 1;

    this.updateItemQuantity(line, quantity, button);
  }

  /**
   * Remove item from cart
   * @param {Event} event - Click event
   */
  removeItem(event) {
    const button = event.currentTarget;
    // Add loading state to the clicked button
    button.classList.add(this.classes.loading);

    const line = parseInt(button.dataset.line, 10);
    this.updateItemQuantity(line, 0, button);
  }

  /**
   * Update item quantity
   * @param {number} line - Line item index
   * @param {number} quantity - New quantity
   * @param {HTMLElement} button - The button that was clicked
   */
  updateItemQuantity(line, quantity, button) {
    const item = this.drawer.querySelector(`[data-line-id="${line}"]`);

    // Show loading state for the item
    if (item) {
      this.showItemLoading(item);
    }

    const data = {
      line,
      quantity,
    };

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then(() => {
        // Update the drawer content
        this.updateCartDrawer();
        this.updateCartCount();
      })
      .catch((error) => {
        console.error('Error updating item quantity:', error);
        // Make sure to remove loading states on error
        if (item) {
          this.hideItemLoading(item);
        }
        if (button) {
          button.classList.remove(this.classes.loading);
        }
      });
  }
}

// Initialize cart drawer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawer = CartDrawer;
  window.cartDrawerInstance = new window.CartDrawer();
});
